import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, HelpCircle, ShieldAlert, Copy } from "lucide-react";
import { sensitiveKindLabel } from "@/lib/instructorImport/sensitive";
import type { SensitiveHit } from "@/lib/instructorImport/types";

type ImportRow = {
  id: string;
  filename: string;
  source_name: string;
  status: string;
  records_detected: number;
  sensitive_flags: SensitiveHit[];
  warnings: string[];
  parser_used: string | null;
  ai_fallback_used: boolean;
};

type RecordRow = {
  id: string;
  row_index: number;
  extracted: any;
  extracted_name: string | null;
  extracted_email: string | null;
  extracted_phone: string | null;
  match_status: string;
  match_confidence: number;
  match_reasons: string[];
  matched_instructor_id: string | null;
  candidate_ids: string[];
  review_status: string;
};

type Instructor = {
  id: string;
  full_name: string;
  preferred_name: string | null;
  email: string | null;
  phone: string | null;
  [k: string]: any;
};

const REVIEWABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: "full_name", label: "Full Name" },
  { key: "preferred_name", label: "Preferred Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "mailing_address", label: "Mailing Address" },
  { key: "emergency_contact_name", label: "Emergency Contact" },
  { key: "emergency_contact_phone", label: "Emergency Phone" },
  { key: "experience", label: "Experience" },
  { key: "pay_rate_type", label: "Pay Rate Type" },
  { key: "pay_rate_amount", label: "Pay Rate Amount" },
  { key: "pay_frequency", label: "Pay Frequency" },
  { key: "payment_method", label: "Payment Method" },
  { key: "zelle_contact", label: "Zelle Contact" },
  { key: "zelle_name", label: "Zelle Name" },
  { key: "payment_notes", label: "Payment Notes" },
  { key: "teaching_agreement_signed", label: "Teaching Agreement" },
  { key: "teaching_agreement_year", label: "Agreement Year" },
  { key: "background_check_completed", label: "Background Check" },
  { key: "background_check_year", label: "BG Check Year" },
  { key: "standards_of_conduct_acknowledged", label: "Standards of Conduct" },
  { key: "w9_status", label: "W-9 Status" },
  { key: "onboarding_status", label: "Onboarding Status" },
];

type Decision = {
  action: "apply" | "skip" | "create_new" | "defer";
  matched_instructor_id?: string | null;
  field_choices: Record<string, { mode: "use_imported" | "keep" | "edit"; edited_value?: any }>;
  aliases: string[];
  create_assignment?: any;
  one_time_payment?: any;
};

export default function ImportReviewScreen({ importId, onClose }: { importId: string; onClose: () => void }) {
  const [imp, setImp] = useState<ImportRow | null>(null);
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [applying, setApplying] = useState(false);
  const [tainted, setTainted] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: iRow }, { data: r2 }, { data: instRows }] = await Promise.all([
        supabase.from("instructor_imports").select("*").eq("id", importId).maybeSingle(),
        supabase.from("instructor_import_records").select("*").eq("import_id", importId).order("row_index"),
        supabase.from("instructors").select("id, full_name, preferred_name, email, phone").order("full_name"),
      ]);
      setImp(iRow as any);
      setRecords((r2 ?? []) as any);
      setInstructors((instRows ?? []) as any);

      // Seed default decisions
      const seed: Record<string, Decision> = {};
      for (const r of (r2 ?? []) as any as RecordRow[]) {
        const action: Decision["action"] =
          r.match_status === "confident" ? "apply"
          : r.match_status === "new" ? "create_new"
          : r.match_status === "invalid" || r.match_status === "duplicate" ? "skip"
          : "defer";
        const fc: Decision["field_choices"] = {};
        for (const f of REVIEWABLE_FIELDS) {
          const v = r.extracted?.[f.key]?.value;
          if (v == null || v === "") continue;
          fc[f.key] = { mode: "use_imported" };
        }
        seed[r.id] = { action, matched_instructor_id: r.matched_instructor_id, field_choices: fc, aliases: [] };
      }
      setDecisions(seed);
    })();
  }, [importId]);

  const instructorById = useMemo(() => new Map(instructors.map(i => [i.id, i])), [instructors]);

  const setDecision = (id: string, patch: Partial<Decision>) => {
    setDecisions(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    setTainted(true);
  };
  const setFieldChoice = (id: string, field: string, mode: "use_imported" | "keep" | "edit", editedValue?: any) => {
    setDecisions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        field_choices: { ...(prev[id]?.field_choices ?? {}), [field]: { mode, edited_value: editedValue } },
      },
    }));
    setTainted(true);
  };

  const summary = useMemo(() => {
    const s = { toUpdate: 0, toCreate: 0, toSkip: 0, deferred: 0, conflicts: 0 };
    for (const r of records) {
      const d = decisions[r.id]; if (!d) continue;
      if (d.action === "skip") s.toSkip++;
      else if (d.action === "create_new") s.toCreate++;
      else if (d.action === "defer") s.deferred++;
      else s.toUpdate++;
      const inst = d.matched_instructor_id ? instructorById.get(d.matched_instructor_id) : null;
      if (inst) {
        for (const [field, choice] of Object.entries(d.field_choices)) {
          const importedVal = r.extracted?.[field]?.value;
          const existingVal = (inst as any)[field];
          if (importedVal != null && existingVal != null && String(importedVal) !== String(existingVal) && choice.mode === "use_imported") {
            s.conflicts++;
          }
        }
      }
    }
    return s;
  }, [records, decisions, instructorById]);

  const apply = async () => {
    setApplying(true);
    try {
      const payload = Object.entries(decisions)
        .filter(([, d]) => d.action !== "defer")
        .map(([record_id, d]) => ({ record_id, ...d }));
      const { data, error } = await supabase.functions.invoke("apply-instructor-import", {
        body: { import_id: importId, decisions: payload },
      });
      if (error) throw error;
      toast.success(`Updated ${data?.profiles_updated ?? 0}, created ${data?.profiles_created ?? 0}, skipped ${data?.skipped ?? 0}.`);
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Import: {imp?.filename}</DialogTitle>
          <DialogDescription>
            Source: {imp?.source_name} • Parser: {imp?.parser_used}{imp?.ai_fallback_used ? " (AI fallback)" : ""} • {records.length} records
          </DialogDescription>
        </DialogHeader>

        {(imp?.sensitive_flags?.length ?? 0) > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm dark:bg-amber-950/30">
            <div className="flex items-center gap-2 font-medium">
              <ShieldAlert className="h-4 w-4" />
              Potential tax documents detected
            </div>
            <div className="text-xs mt-1">
              This upload appears to contain: {imp!.sensitive_flags.map(f => `${sensitiveKindLabel(f.kind)} (${f.location})`).join(", ")}.
              Tax documents belong in the secure Onboarding / W-9 workflow, not the general import tool.
              Contact information and other non-tax fields have been kept; taxpayer identification numbers were not extracted.
            </div>
          </div>
        )}

        {(imp?.warnings?.length ?? 0) > 0 && (
          <div className="text-xs text-muted-foreground">
            Warnings: {imp!.warnings.join("; ")}
          </div>
        )}

        <div className="space-y-4">
          {records.map((r) => {
            const d = decisions[r.id];
            if (!d) return null;
            const inst = d.matched_instructor_id ? instructorById.get(d.matched_instructor_id) : null;
            const statusTone =
              r.match_status === "confident" ? "border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/20"
              : r.match_status === "possible" ? "border-amber-500/40 bg-amber-50/40 dark:bg-amber-950/20"
              : r.match_status === "new" ? "border-sky-500/40 bg-sky-50/40 dark:bg-sky-950/20"
              : r.match_status === "duplicate" ? "border-muted bg-muted/30"
              : "border-destructive/40 bg-destructive/5";
            return (
              <Card key={r.id} className={statusTone}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base flex items-center gap-2">
                      {r.extracted_name ?? "(no name)"} <Badge variant="outline">{r.match_status}</Badge>
                      <span className="text-xs text-muted-foreground">confidence {r.match_confidence}%</span>
                    </CardTitle>
                    <div className="flex gap-2">
                      <Select value={d.action} onValueChange={(v) => setDecision(r.id, { action: v as any })}>
                        <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apply">Apply to matched</SelectItem>
                          <SelectItem value="create_new">Create new profile</SelectItem>
                          <SelectItem value="skip">Skip</SelectItem>
                          <SelectItem value="defer">Defer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {r.match_reasons.length > 0 && (
                    <div className="text-xs text-muted-foreground">{r.match_reasons.join(" • ")}</div>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {d.action === "apply" && (
                    <div className="flex items-center gap-2 text-xs">
                      <Label className="text-xs">Matched profile:</Label>
                      <Select
                        value={d.matched_instructor_id ?? ""}
                        onValueChange={(v) => setDecision(r.id, { matched_instructor_id: v })}
                      >
                        <SelectTrigger className="h-8 text-xs w-72"><SelectValue placeholder="Choose profile" /></SelectTrigger>
                        <SelectContent>
                          {(r.candidate_ids.length ? r.candidate_ids : instructors.slice(0, 50).map(i => i.id))
                            .map(id => instructorById.get(id))
                            .filter(Boolean)
                            .map(i => (
                              <SelectItem key={i!.id} value={i!.id}>
                                {i!.preferred_name ?? i!.full_name}{i!.email ? ` — ${i!.email}` : ""}
                              </SelectItem>
                            ))}
                          {instructors.filter(i => !r.candidate_ids.includes(i.id)).slice(0, 200).map(i => (
                            <SelectItem key={i.id} value={i.id}>
                              {i.preferred_name ?? i.full_name}{i.email ? ` — ${i.email}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {(d.action === "apply" || d.action === "create_new") && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-muted-foreground">
                          <tr>
                            <th className="text-left py-1 pr-2">Field</th>
                            <th className="text-left py-1 pr-2">Existing</th>
                            <th className="text-left py-1 pr-2">Imported</th>
                            <th className="text-left py-1">Decision</th>
                          </tr>
                        </thead>
                        <tbody>
                          {REVIEWABLE_FIELDS.map((f) => {
                            const importedVal = r.extracted?.[f.key]?.value;
                            if (importedVal == null || importedVal === "") return null;
                            const existingVal = inst ? (inst as any)[f.key] : null;
                            const conflict = existingVal != null && String(existingVal) !== String(importedVal);
                            const choice = d.field_choices[f.key] ?? { mode: "use_imported" as const };
                            const conf = r.extracted?.[f.key]?.confidence ?? 100;
                            const needsReview = r.extracted?.[f.key]?.needs_review || conf < 70;
                            return (
                              <tr key={f.key} className="border-t">
                                <td className="py-1 pr-2 font-medium">
                                  {f.label}
                                  {needsReview && <HelpCircle className="inline h-3 w-3 ml-1 text-amber-600" />}
                                </td>
                                <td className="py-1 pr-2 text-muted-foreground">{existingVal ?? "—"}</td>
                                <td className={`py-1 pr-2 ${conflict ? "text-amber-700 font-medium" : ""}`}>
                                  {String(importedVal)}
                                  {conflict && <AlertTriangle className="inline h-3 w-3 ml-1" />}
                                </td>
                                <td className="py-1">
                                  <div className="flex items-center gap-1">
                                    <Select
                                      value={choice.mode}
                                      onValueChange={(m) => setFieldChoice(r.id, f.key, m as any, choice.edited_value)}
                                    >
                                      <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="use_imported">Use Imported</SelectItem>
                                        <SelectItem value="keep">Keep Existing</SelectItem>
                                        <SelectItem value="edit">Edit</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    {choice.mode === "edit" && (
                                      <Input
                                        className="h-7 text-xs w-40"
                                        defaultValue={String(choice.edited_value ?? importedVal ?? "")}
                                        onBlur={(e) => setFieldChoice(r.id, f.key, "edit", e.target.value)}
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-background pt-3 border-t">
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="flex flex-wrap gap-3 text-xs">
              <Badge variant="secondary"><CheckCircle2 className="h-3 w-3 mr-1" />{summary.toUpdate} update</Badge>
              <Badge variant="secondary"><Copy className="h-3 w-3 mr-1" />{summary.toCreate} create</Badge>
              <Badge variant="outline">{summary.toSkip} skip</Badge>
              <Badge variant="outline">{summary.deferred} defer</Badge>
              {summary.conflicts > 0 && <Badge variant="destructive">{summary.conflicts} conflicts using imported</Badge>}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} disabled={applying}>Cancel Import</Button>
              <Button onClick={apply} disabled={applying}>{applying ? "Applying…" : "Apply Approved Import"}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
